import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { LoginSchema } from '@/lib/schemas/auth';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { CosmoBudLogo } from '@/components/CosmoBudLogo';
import { ParticlesBackground } from '@/components/ParticlesBackground';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface LocationState {
  from?: string;
}

function errorMessage(code: string): string {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return 'Email or password is incorrect.';
    case 'ACCOUNT_LOCKED':
      return 'Account temporarily locked after too many failed attempts. Try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  useEffect(() => {
    if (status === 'authed' || status === 'must_change') {
      const next = (location.state as LocationState | null)?.from;
      navigate(status === 'must_change' ? '/change-password' : (next ?? '/'), { replace: true });
    }
  }, [status, navigate, location.state]);

  if (status === 'loading') {
    return <div className="grid min-h-[40vh] place-items-center text-sm text-slate-500">Loading…</div>;
  }
  if (status === 'authed') return <Navigate to="/" replace />;
  if (status === 'must_change') return <Navigate to="/change-password" replace />;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorText(null);
    const parsed = LoginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fe: { email?: string; password?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'email') fe.email = issue.message;
        if (key === 'password') fe.password = issue.message;
      }
      setFieldErrors(fe);
      return;
    }
    setFieldErrors({});
    setBusy(true);
    try {
      await login(parsed.data.email, parsed.data.password);
      // Status update flows through useEffect above.
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorText(errorMessage(err.errorCode));
      } else {
        setErrorText('Network error. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Desktop: Left side brand area (50%) */}
      <div className="hidden sm:flex flex-1 relative items-center justify-center p-8 overflow-hidden bg-white">
        <ParticlesBackground />
        <div className="relative z-10 text-center">
          <img src="/cosmobud-hero.png" alt="CosmoBud" className="w-64 h-auto drop-shadow-lg" />
        </div>
      </div>

      {/* Mobile: Full width form / Desktop: Right side (50%) */}
      <div className="flex flex-col w-full sm:w-1/2 min-h-screen sm:bg-gradient-to-br sm:from-blue-600 sm:to-violet-600">
        <div className="flex flex-col min-h-screen bg-background sm:bg-transparent px-4 pt-6 pb-6 sm:px-8 sm:py-0 sm:place-items-center sm:justify-center">
      <Card className="w-full rounded-none sm:rounded-lg sm:max-w-md sm:bg-white sm:shadow-2xl">
        <CardHeader className="px-4 pt-6 pb-4 sm:pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex justify-center sm:hidden">
              <CosmoBudLogo size="custom" showText={false} customSize="w-[60px] h-[60px]" />
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <CardTitle className="text-lg sm:text-xl">Sign in</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Enter your email and password.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-6 sm:pb-4">
          {errorText && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Sign-in failed</AlertTitle>
              <AlertDescription>{errorText}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                aria-invalid={fieldErrors.email ? true : undefined}
              />
              {fieldErrors.email && (
                <p className="text-xs text-red-600">{fieldErrors.email}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                aria-invalid={fieldErrors.password ? true : undefined}
              />
              {fieldErrors.password && (
                <p className="text-xs text-red-600">{fieldErrors.password}</p>
              )}
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-brand-600 hover:text-brand-700 font-medium cursor-pointer"
            >
              Forgot password?
            </button>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
}
